import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";

/**
 * Premium confirmation dialog used everywhere a destructive / important
 * action is performed (block, unblock, remove, logout, delete, accept, etc.)
 *
 * tone: 'brand' | 'danger' | 'warning' | 'success'
 */
const tones = {
  brand:   { iconBg: "bg-brand-50",   iconText: "text-brand-600",   button: "btn-primary" },
  danger:  { iconBg: "bg-rose-50",    iconText: "text-rose-600",    button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-rose-700 active:scale-[0.98] disabled:opacity-60" },
  warning: { iconBg: "bg-amber-50",   iconText: "text-amber-600",   button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-amber-600 active:scale-[0.98] disabled:opacity-60" },
  success: { iconBg: "bg-emerald-50", iconText: "text-emerald-600", button: "inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-emerald-700 active:scale-[0.98] disabled:opacity-60" },
};

export default function ConfirmDialog({
  open,
  title,
  description,
  Icon = AlertTriangle,
  tone = "brand",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  onConfirm,
  onClose,
  children,
}) {
  const t = tones[tone] || tones.brand;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !loading && onClose?.()}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/60 px-4 backdrop-blur-md"
        >
          <motion.div
            initial={{ y: 32, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated"
          >
            <div className="px-5 pt-5 text-center">
              <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl ${t.iconBg} ${t.iconText}`}>
                <Icon size={24} />
              </div>
              <h3 className="mt-3 font-display text-lg font-bold text-ink-800">{title}</h3>
              {description && (
                <p className="mt-1.5 text-[13px] leading-6 text-ink-500">{description}</p>
              )}
            </div>

            {children && <div className="px-5 pt-3">{children}</div>}

            <div className="grid grid-cols-2 gap-3 px-5 py-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-2xl border border-sand-200 bg-white py-2.5 text-[13px] font-semibold text-ink-700 transition hover:bg-sand-50"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className={t.button}
              >
                {loading ? "Please wait..." : confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
